import { Badge, type BadgeProps } from '@proerp/ui';
import {
  canCreateCreditNote,
  isTerminalSalesStatus,
  manualStatusesForKind,
  manualStatusTargetsFor,
  type SalesDocumentKind,
  type SalesDocumentStatus,
} from '@proerp/shared';

const KIND_LABELS: Record<SalesDocumentKind, string> = {
  QUOTE: 'Angebot',
  ORDER: 'Auftrag',
  INVOICE: 'Rechnung',
  ADVANCE_INVOICE: 'Anzahlungsrechnung',
  FINAL_INVOICE: 'Schlussrechnung',
  DELIVERY_NOTE: 'Lieferschein',
  PRO_FORMA: 'Pro-forma-Rechnung',
  CREDIT_NOTE: 'Stornorechnung',
  PICKING_LIST: 'Kommissionierliste',
};

export function kindLabel(kind: SalesDocumentKind): string {
  return KIND_LABELS[kind];
}

/** Belegart auf Englisch — dieselben Titel wie im englischen PDF (`sales-pdf.service.ts`). */
const KIND_LABELS_EN: Record<SalesDocumentKind, string> = {
  QUOTE: 'Quotation',
  ORDER: 'Order Confirmation',
  INVOICE: 'Invoice',
  ADVANCE_INVOICE: 'Advance Invoice',
  FINAL_INVOICE: 'Final Invoice',
  DELIVERY_NOTE: 'Delivery Note',
  PRO_FORMA: 'Pro Forma Invoice',
  CREDIT_NOTE: 'Credit Note',
  PICKING_LIST: 'Picking List',
};

/**
 * Betreff/Titel des Mail-Entwurfs in der Belegsprache (Review 2026-09-28,
 * Befund 9) — „Invoice RE-…" statt „Rechnung RE-…" für einen englischen Beleg.
 */
export function salesMailTitle(doc: { kind: SalesDocumentKind; number: string; language: 'DE' | 'EN' }): string {
  return `${doc.language === 'EN' ? KIND_LABELS_EN[doc.kind] : KIND_LABELS[doc.kind]} ${doc.number}`;
}

/** Tabs/Filter der Belegliste. */
export const KIND_TABS: { kind: SalesDocumentKind; label: string }[] = [
  { kind: 'QUOTE', label: 'Angebote' },
  { kind: 'ORDER', label: 'Aufträge' },
  { kind: 'DELIVERY_NOTE', label: 'Lieferscheine' },
  { kind: 'INVOICE', label: 'Rechnungen' },
  { kind: 'ADVANCE_INVOICE', label: 'Anzahlungen' },
  { kind: 'FINAL_INVOICE', label: 'Schlussrechnungen' },
  { kind: 'PRO_FORMA', label: 'Pro-forma' },
  { kind: 'CREDIT_NOTE', label: 'Stornos' },
  // „Kommissionierlisten" (PICKING_LIST) entfernt — ersetzt durch das Modul
  // „Kommissionierung" (Tab „Kommissionierung"). Enum-Wert bleibt für Altdaten bestehen.
];

/**
 * Belegarten für den Anlege-Dialog: ohne Schluss- und Anzahlungsrechnung — beide
 * entstehen seit ADR 0077 nur noch aus dem Zahlungsplan des Auftrags (nur dort
 * werden Anzahlungen verrechnet; das Backend lehnt beide freistehend ab, Review
 * 2026-09-28 Befund 4). Die Tabs bleiben für Altbelege sichtbar.
 */
export const CREATABLE_KIND_TABS = KIND_TABS.filter(
  (tab) => tab.kind !== 'FINAL_INVOICE' && tab.kind !== 'ADVANCE_INVOICE',
);

/** Anlegbare Belegart absichern: nicht anlegbare Arten fallen auf „Rechnung" zurück. */
export function creatableKind(kind: SalesDocumentKind): SalesDocumentKind {
  return CREATABLE_KIND_TABS.some((tab) => tab.kind === kind) ? kind : 'INVOICE';
}

const STATUS_META: Record<SalesDocumentStatus, { tone: BadgeProps['tone']; label: string }> = {
  DRAFT: { tone: 'neutral', label: 'Entwurf' },
  SENT: { tone: 'info', label: 'Versendet' },
  ACCEPTED: { tone: 'success', label: 'Angenommen' },
  REJECTED: { tone: 'danger', label: 'Abgelehnt' },
  PAID: { tone: 'success', label: 'Bezahlt' },
  CANCELLED: { tone: 'neutral', label: 'Storniert' },
};

export const SALES_STATUS_OPTIONS = (Object.keys(STATUS_META) as SalesDocumentStatus[]).map(
  (value) => ({ value, label: STATUS_META[value].label }),
);

/** Menschenlesbares Label eines Status. */
export function statusLabel(status: SalesDocumentStatus): string {
  return STATUS_META[status].label;
}

/**
 * Manuell wählbare Status je Belegart (#29) — deckt sich mit der Backend-
 * Validierung (ALLOWED_STATUSES_BY_KIND) und lässt „Bezahlt" weg, da dieser
 * Status nur automatisch über den Zahlungseingang gesetzt wird.
 */
export function statusOptionsForKind(
  kind: SalesDocumentKind,
): { value: SalesDocumentStatus; label: string }[] {
  return manualStatusesForKind(kind).map((value) => ({ value, label: STATUS_META[value].label }));
}

/**
 * Manuell wählbare Zielstatus für einen KONKRETEN Beleg: terminale Ist-Status
 * („Bezahlt"/„Storniert") werden manuell nicht mehr verlassen, und der
 * Übergangsgraph aus `packages/shared` gilt auch hier (Audit 2026-08-05) —
 * eine versendete Rechnung bekommt kein „Entwurf" mehr angeboten, ein
 * Lieferschein kein „Versendet" (das bucht nur der Warenausgang). Das Menü
 * zeigt damit nichts, was der Server ablehnen würde.
 */
export function statusOptionsForDocument(
  kind: SalesDocumentKind,
  currentStatus: SalesDocumentStatus,
): { value: SalesDocumentStatus; label: string }[] {
  if (isTerminalSalesStatus(currentStatus)) return [];
  return manualStatusTargetsFor(kind, currentStatus).map((value) => ({
    value,
    label: STATUS_META[value].label,
  }));
}

/**
 * Anzahlungs-/Schlussrechnung nur aus einem „lebenden" Auftrag (Spiegel des
 * Backends): bei storniertem/abgelehntem Auftrag verschwinden die Menüpunkte.
 */
export function canCreatePartialInvoices(doc: {
  kind: SalesDocumentKind;
  status: SalesDocumentStatus;
}): boolean {
  return doc.kind === 'ORDER' && doc.status !== 'CANCELLED' && doc.status !== 'REJECTED';
}

export function SalesStatusBadge({ status }: { status: SalesDocumentStatus }) {
  const { tone, label } = STATUS_META[status];
  return <Badge tone={tone}>{label}</Badge>;
}

const CONVERT_LABELS: Record<SalesDocumentKind, string> = {
  QUOTE: 'Angebot',
  ORDER: 'Auftrag',
  INVOICE: 'Rechnung',
  ADVANCE_INVOICE: 'Anzahlungsrechnung',
  FINAL_INVOICE: 'Schlussrechnung',
  DELIVERY_NOTE: 'Lieferschein',
  PRO_FORMA: 'Pro-forma',
  CREDIT_NOTE: 'Storno',
  PICKING_LIST: 'Kommissionierliste',
};

/** Erlaubte Umwandlungsziele je Belegart (Spiegel des Backends, ADR 0007).
 * Anzahlungs-/Schlussrechnung laufen über eigene Endpunkte, nicht über convert. */
const ALLOWED_CONVERSIONS: Record<SalesDocumentKind, SalesDocumentKind[]> = {
  QUOTE: ['ORDER', 'PRO_FORMA'],
  // ORDER → INVOICE als Direktweg entfernt (ADR 0077) — der Zahlungsplan-Reiter
  // im Editor ist jetzt der einzige Weg, aus einem Auftrag eine Rechnung zu
  // erzeugen (Spiegel von ALLOWED_CONVERSIONS in sales-document.service.ts).
  ORDER: ['DELIVERY_NOTE', 'PRO_FORMA'],
  DELIVERY_NOTE: ['INVOICE'],
  PRO_FORMA: ['INVOICE'],
  INVOICE: ['CREDIT_NOTE'],
  ADVANCE_INVOICE: ['CREDIT_NOTE'],
  FINAL_INVOICE: ['CREDIT_NOTE'],
  CREDIT_NOTE: [],
  PICKING_LIST: [],
};

/**
 * Wandel-Ziele für einen konkreten Beleg. „Stornieren" (Stornorechnung) nur zu
 * einer ausgestellten Rechnung — ein Entwurf wird gelöscht (Spiegel von
 * `canCreateCreditNote`, das auch der Server prüft; Review 2026-09-28).
 */
export function convertTargets(
  kind: SalesDocumentKind,
  status: SalesDocumentStatus,
): { kind: SalesDocumentKind; label: string }[] {
  return ALLOWED_CONVERSIONS[kind]
    .filter((target) => target !== 'CREDIT_NOTE' || canCreateCreditNote({ kind, status }))
    .map((target) => ({
      kind: target,
      label: target === 'CREDIT_NOTE' ? 'Stornieren' : `→ ${CONVERT_LABELS[target]}`,
    }));
}

/**
 * Erfolgsmeldung nach dem Wandeln. Bei der Stornorechnung mit dem Hinweis, dass
 * die Rechnung erst mit deren Versand storniert ist (Befund 2a).
 */
export function convertSuccessMessage(
  created: { kind: SalesDocumentKind; number: string },
  sourceNumber: string,
): string {
  const base = `${kindLabel(created.kind)} ${created.number} erstellt.`;
  return created.kind === 'CREDIT_NOTE'
    ? `${base} ${sourceNumber} gilt als storniert, sobald die Stornorechnung versendet ist.`
    : base;
}
